import { ImageContent } from "@obg/ngx.core.content";

export const enum RewardStateType {
	Unknown = "Unknown",
	Claimable = "Claimable",
	Active = "Active",
	Expired = "Expired",
	Completed = "Completed",
	PendingPayout = "PendingPayout",
	Forfeited = "Forfeited",
	EndBalanceReached = "EndBalanceReached",
}

export const enum RewardStatePayload {
	Active = "Active",
	Ended = "Ended",
}

export enum RewardStateFilterType {
	Claimable = "claimable",
	Active = "active",
	Pending = "pending",
	Ended = "ended",
}

export const enum RewardLockupType {
	LockUpDepositBonusType = "LockupBonusReward",
	LockUpLegacyBonusType = "PercentBonusDefinition",
	PlaytechPokerReward = "PlaytechPokerReward",
	TokenReward = "TokenReward",
}

export const enum RewardPayoutStrategyType {
	LockUpPayoutStrategy = "PayoutOnCustomerClaim",
	DepositPayoutStrategy = "PayoutOnDeposit",
}

export const enum RewardCategory {
	Bonus = "Bonus",
	Cash = "Cash",
	FreeSpins = "FreeSpins",
	FreeBet = "FreeBet",
	RiskFreeBet = "RiskFreeBet",
	PriceBoost = "PriceBoost",
	Poker = "Poker",
	Other = "Other",
}

export interface RewardState {
	rewards: Rewards;
}

export interface Rewards extends Record<string, Reward[] | undefined> {
	active?: Reward[];
	claimable?: Reward[];
	ended?: Reward[];
	pending?: Reward[];
}

export interface RewardUpdatePayload {
	reward: Reward;
	claimAmount?: number;
	currencyCode?: string;
	bonusCode?: string;
	siteTrackingId?: string;
}

export interface RewardUpdateRequest {
	state: RewardStatePayload;
	data?: RewardUpdateRequestData;
}

export interface RewardClaimRequest {
	siteTrackingId?: string;
}

export interface RewardUpdateRequestData {
	siteTrackingId?: string;
	amount?: number;
	currencyCode?: string;
	bonusCode?: string;
}

export interface Reward {
	bonusContext: string[];
	currencyCode: string;
	description?: string;
	identifier: string;
	image?: ImageContent;
	objectType: string;
	state: RewardStateType;
	stateFilter: RewardStateFilterType;
	termsAndConditions?: string;
	title?: string;
	amountWagered?: number;
	claimableUntilDate?: string;
	claimedDate?: string;
	currentBonusAmount?: number;
	currentWageringRequirement?: number;
	hasWageringProgress?: boolean;
	depositReturnPercentage?: number;
	expiryDate?: string;
	forfeitedDate?: string;
	initialBonusAmount?: number;
	isLockUpBonus?: boolean;
	isRealMoneyBonus?: boolean;
	maxAmountToWager?: number;
	maxClaimAmount?: number;
	minClaimAmount?: number;
	payoutStrategy?: RewardPayoutStrategy;
	percentage?: number;
	percentageComplete?: number;
	rewardCategory?: RewardCategory;
	groupedBonusContexts?: string[];
}

export interface RewardsPayload {
	rewards: Rewards;
	stateFilters?: RewardStateFilterType[];
}

export interface RewardFilter {
	identifier: string;
	objectType: string;
}

export interface RewardSelectionFilter {
	identifier: string;
	objectType: string;
	stateFilter?: RewardStateFilterType;
}

export interface RewardsResponse extends Record<string, RewardResponse[] | undefined> {
	active?: RewardResponse[];
	pending?: RewardResponse[];
	claimable?: RewardResponse[];
	ended?: RewardResponse[];
}

export interface RewardPayoutStrategy {
	paymentMethodName?: string;
	excludedPaymentMethods?: string[];
	strategy: string;
}

export interface RewardResponse {
	objectType: string; // might be made enum, currently values may change
	state: RewardStateType;
	identifier: string;
	currencyCode: string;
	content: RewardContent;
	initialRealBonusMoney?: number;
	initialSpecialBonusMoney?: number;
	initialBonusAmount?: number;
	initialBonusMoney?: number;
	currentBonusAmount?: number;
	currentBonusMoney?: number;
	currentRealBonusMoney?: number;
	specialBonusMoney?: number;
	isRealMoneyBonus?: boolean;
	claimedDate?: string;
	currentLockedAmount?: number;
	expires?: string;
	claimableTo?: string;
	forfeitedDate?: string;
	payoutStrategy?: RewardPayoutStrategy;
	percent?: number;
	percentage?: number;
	minClaimAmount?: number;
	maxClaimAmount?: number;
	minWageredAmount?: number;
	maxRecievedAmount?: number;
	initialWageringRequirement?: number;
	currentWageringRequirement?: number;
	hasWageringRequirement?: boolean;
	bonusContexts?: string[];
	lockupAmount?: number;
	linkedId?: string;
	rewardCategory?: RewardCategory;
	groupedBonusContexts?: string[];
}

export interface RewardContent {
	textContentItems?: RewardTextContent;
	imageContentItems?: RewardImageContent;
}

export interface RewardTextContent {
	title: string;
	description: string;
	termsAndConditions: string;
}

export interface RewardImageContent {
	image: ImageContent[];
}

export interface RewardCount {
	claimable: number;
	active: number;
	ended: number;
}
